import { RoleTypes } from '@/portainer/rbac/models/role';

import { isIdForNewRow } from '@@/datatables/editable/actionsColumn';

import {
  KubernetesRbacFormValues,
  KubernetesRbacPolicyPayload,
  KubernetesAccessPolicyValue,
  type KubernetesUnifiedAccess,
} from './types';

// Namespace role IDs (Standard=3, Read-only=4, Namespace Operator=6)
const NAMESPACE_ROLE_IDS = [
  RoleTypes.STANDARD,
  RoleTypes.READ_ONLY,
  RoleTypes.NAMESPACE_OPERATOR,
] as const;

function isNamespaceRole(
  roleId: number
): roleId is (typeof NAMESPACE_ROLE_IDS)[number] {
  return NAMESPACE_ROLE_IDS.includes(
    roleId as (typeof NAMESPACE_ROLE_IDS)[number]
  );
}

/**
 * Builds the API payload from form data
 * All users/teams go into EnvironmentAccessPolicies
 * Namespace-scoped roles include Namespaces in their policy value
 */
export function buildKubernetesRBACPayload(
  formData: KubernetesRbacFormValues
): KubernetesRbacPolicyPayload {
  const accessList = formData.accessList || [];
  // strip out incomplete data rows, should not get here past form validation
  function isValid(access: KubernetesUnifiedAccess) {
    return (
      !isIdForNewRow(access.Id) &&
      access.PartyId !== null &&
      access.Role.Id !== null &&
      access.Type !== ''
    );
  }

  return {
    EnvironmentAccessPolicies: {
      UserAccessPolicies: buildAccessPolicies(
        accessList.filter((access) => isValid(access) && access.Type === 'user')
      ),
      TeamAccessPolicies: buildAccessPolicies(
        accessList.filter((access) => isValid(access) && access.Type === 'team')
      ),
    },
  };
}

function buildAccessPolicies(entries: KubernetesUnifiedAccess[]) {
  return Object.fromEntries(
    entries.map((entry) => {
      const policyValue: KubernetesAccessPolicyValue = {
        RoleId: entry.Role.Id!,
      };

      if (isNamespaceRole(entry.Role.Id!) && entry.namespaces) {
        policyValue.Namespaces = entry.namespaces;
      }

      return [
        entry.PartyId!.toString(),
        {
          value: policyValue,
          enforce: false,
        },
      ];
    })
  );
}
